import React, { useState, useEffect } from 'react';
import { collectionRequestAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './CollectionPage.css';

interface CollectionFormData {
  collectionDate: string;
  collectionTime: string;
  address: string;
  specialInstructions: string;
}

interface UserAddress {
  street: string;
  detail: string;
  zipCode: string;
}

export function CollectionPage() {
  const { user } = useAuth();
  const [formData, setFormData] = useState<CollectionFormData>({
    collectionDate: '',
    collectionTime: '',
    address: '',
    specialInstructions: ''
  });

  const [userAddress, setUserAddress] = useState<UserAddress | null>(null);
  
  // 사용자 정보에서 주소 로드
  useEffect(() => {
    if (user) {
      if (user.address_street) {
        setUserAddress({
          street: user.address_street,
          detail: user.address_detail || '',
          zipCode: user.address_zipcode || ''
        });
      }
    }
  }, [user]);

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressEdit = () => {
    // 주소가 없으면 빈 주소로 초기화
    if (!userAddress) {
      setUserAddress({
        street: '',
        detail: '',
        zipCode: ''
      });
    }
    setIsEditingAddress(true);
  };

  const handleAddressSave = async () => {
    if (!userAddress) return;
    
    try {
              const response = await fetch('http://localhost:8002/auth/me/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          address_street: userAddress.street,
          address_detail: userAddress.detail,
          address_zipcode: userAddress.zipCode
        })
      });
      
      if (response.ok) {
        setIsEditingAddress(false);
        setSubmitMessage('주소가 성공적으로 저장되었습니다!');
        // 3초 후 메시지 제거
        setTimeout(() => setSubmitMessage(''), 3000);
      } else {
        const error = await response.json();
        setSubmitMessage(error.detail || '주소 저장에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to save address:', error);
      setSubmitMessage('주소 저장 중 오류가 발생했습니다');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // 주소가 없으면 주소 입력을 요구
    if (!userAddress) {
      setSubmitMessage('수거 주소를 먼저 등록해주세요.');
      setIsSubmitting(false);
      setIsEditingAddress(true);
      return;
    }

    try {
      // API 호출로 수거 요청 제출
      const requestData = {
        waste_type: 'food', // 음식물 폐기물 고정
        description: `수거 희망일: ${formData.collectionDate} ${formData.collectionTime}${formData.specialInstructions ? '\n특별 요청사항: ' + formData.specialInstructions : ''}`,
        collection_address: `${userAddress.street} ${userAddress.detail} (${userAddress.zipCode})`,
        collection_date: `${formData.collectionDate}T${formData.collectionTime}:00`,
        urgency: 'medium',
        special_instructions: formData.specialInstructions,
        is_recurring: false
      };
      
      await collectionRequestAPI.createRequest(requestData);
      
      setSubmitMessage('음식물 폐기물 수거 요청이 성공적으로 제출되었습니다!');
      setFormData({
        collectionDate: '',
        collectionTime: '',
        address: '',
        specialInstructions: ''
      });
    } catch (error: any) {
      console.error('Failed to submit request:', error);
      setSubmitMessage(error.response?.data?.detail || '수거 요청 제출에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.collectionDate && formData.collectionTime && userAddress;

  return (
    <div className="collection-page">
      <div className="collection-container">
        <div className="collection-header">
          <h1>🥬 음식물 폐기물 수거 요청</h1>
          <p>음식물 폐기물 수거 서비스를 요청하실 수 있습니다. 간편하게 신청해보세요!</p>
        </div>

        <div className="collection-content">
          <form onSubmit={handleSubmit} className="collection-form">
            {/* 수거 주소 섹션 */}
            <div className="form-section">
              <h2>📍 수거 주소</h2>
              {userAddress ? (
                <div className="address-display">
                  <div className="current-address">
                    <div className="address-text">
                      <strong>{userAddress.street}</strong><br />
                      {userAddress.detail}<br />
                      <span className="zip-code">우편번호: {userAddress.zipCode}</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddressEdit}
                      className="edit-address-button"
                    >
                      주소 수정
                    </button>
                  </div>
                  {isEditingAddress && (
                    <div className="address-edit-form">
                      <div className="form-group">
                        <label>도로명 주소</label>
                        <input
                          type="text"
                          value={userAddress.street}
                          onChange={(e) => setUserAddress(prev => prev ? {...prev, street: e.target.value} : null)}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>상세 주소</label>
                        <input
                          type="text"
                          value={userAddress.detail}
                          onChange={(e) => setUserAddress(prev => prev ? {...prev, detail: e.target.value} : null)}
                          className="form-input"
                          placeholder="동, 호수 등"
                        />
                      </div>
                      <div className="form-group">
                        <label>우편번호</label>
                        <input
                          type="text"
                          value={userAddress.zipCode}
                          onChange={(e) => setUserAddress(prev => prev ? {...prev, zipCode: e.target.value} : null)}
                          className="form-input"
                        />
                      </div>
                      <div className="address-buttons">
                        <button type="button" onClick={handleAddressSave} className="save-address-button">
                          저장
                        </button>
                        <button type="button" onClick={() => setIsEditingAddress(false)} className="cancel-address-button">
                          취소
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-address">
                  <p>등록된 수거 주소가 없습니다.</p>
                  <button type="button" onClick={handleAddressEdit} className="add-address-button">
                    주소 등록하기
                  </button>
                </div>
              )}
            </div>

            {/* 수거 일정 */}
            <div className="form-section">
              <h2>📅 수거 일정</h2>
              <div className="food-waste-info">
                <div className="waste-type-display">
                  <span className="waste-icon">🥬</span>
                  <span className="waste-label">음식물 폐기물</span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="collectionDate">수거 희망일 *</label>
                  <input
                    type="date"
                    id="collectionDate"
                    name="collectionDate"
                    value={formData.collectionDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="collectionTime">수거 희망시간 *</label>
                  <select
                    id="collectionTime"
                    name="collectionTime"
                    value={formData.collectionTime}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">시간 선택</option>
                    <option value="09:00">오전 9시</option>
                    <option value="10:00">오전 10시</option>
                    <option value="11:00">오전 11시</option>
                    <option value="12:00">오후 12시</option>
                    <option value="13:00">오후 1시</option>
                    <option value="14:00">오후 2시</option>
                    <option value="15:00">오후 3시</option>
                    <option value="16:00">오후 4시</option>
                    <option value="17:00">오후 5시</option>
                    <option value="18:00">오후 6시</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 특별 요청사항 */}
            <div className="form-section">
              <h2>📝 특별 요청사항</h2>
              <div className="form-group">
                <label htmlFor="specialInstructions">추가 요청사항 (선택사항)</label>
                <textarea
                  id="specialInstructions"
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="특별한 요청사항이 있으시면 입력해주세요"
                  rows={4}
                />
              </div>
            </div>

            <div className="form-actions">
              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('성공') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" color="white" />
                    수거 요청 중...
                  </>
                ) : (
                  '🥬 음식물 폐기물 수거 요청'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}